import { useState, useEffect } from "react";
import { addressFormatter } from "../Components/Formatting/Address";
import { Navigate } from "react-router-dom";
import API from "./API";
import { useMobile } from "../Components/Mobile";

const isLocal = () => {
    return parseInt(window.location.port) > 1000;
}

const deviceIsMd = () => {
    return window.innerWidth >= 768
}

const hashCode = (...args) => {
    const str = JSON.stringify(args);

    let hash = 0;
    for (let i = 0, len = str.length; i < len; i++) {
        let chr = str.charCodeAt(i);
        hash = (hash << 5) - hash + chr;
        hash |= 0; // Convert to 32bit integer
    }

    return Math.abs(hash).toString(36);
}

const has = (value, hasVal = true, hasntVal = false) => {
    if (!value) {
        return hasntVal;
    }

    if (typeof value) {
        return value.trim().length ? hasVal : hasntVal;
    }

    return !!value ? hasVal : hasntVal;
}

const classNames = (...classes) => {
    return classes.filter(Boolean).join(' ')
}

const toQueryString = (obj) => {
    if (!obj) {
        return '';
    }

    return Object.keys(obj).reduce((search, key, i) => {
        const val = obj[key];

        if (Array.isArray(val)) {
            val.forEach((v) => {
                search.append(`${key}[]`, v);
            });
        } else {
            search.append(key, val);
        }

        return search;
    }, new URLSearchParams()).toString();
}

const getEntityLink = (model, id) => {

    const map ={
        'Quotes/Quote':                 '/manage/quotes/' + id,
        'Payments/ConnectedBank':       '/settings/payments/connected-banks',
        'Payments/Account':             '/payments',
        'Payments/Payment':             '/manage/payments/' + id,
        'Balances/Balance':             '/balance/',
        'Marketplace/Subscription':     '/marketplace/subscription',
        'Marketplace/Lead':             '/plan/leads/' + id,
        'Customers/Customer':           '/manage/customers/' + id,
        'Rounds/Round':                 '/plan/rounds/' + id,
        'Services/Service':             '/business/services/' + id,
        'Templates/Template':           '/settings/message-templates/' + id,
        'Soapy/WhatsNewNotification':   '/whats-new',
        'Documents/Document':   '/business/documents/' + id,
    }

    return map[model] ?? model;
}

const useVisibilityChange = (handleVisibilityChange, onlyVisible = true) => {
    const onVisibilityChange = () => {
        let eventName;
        if (typeof document.hidden !== 'undefined') {
            eventName = 'visibilitychange';
        } else if (typeof document.msHidden !== 'undefined') {
            eventName = 'msvisibilitychange';
        } else if (typeof document.webkitHidden !== 'undefined') {
            eventName = 'webkitvisibilitychange'
        }

        const theFunction = () => {
            if (onlyVisible && isDocumentHidden()) {
                return
            }
            handleVisibilityChange(isDocumentHidden())
        };

        document.addEventListener(eventName, theFunction, false);

        return () => {
            document.removeEventListener(eventName, theFunction, false)
        };
    }

    useEffect(() => {
        return onVisibilityChange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
}

const isDocumentHidden = () => {
    if (typeof document.hidden !== 'undefined') {
        return document['hidden'];
    } else if (typeof document.msHidden !== 'undefined') {
        return document['msHidden'];
    } else if (typeof document.webkitHidden !== 'undefined') {
        return document['webkitHidden'];
    }
}

const weekString = (_dt = null) => {
    let dt = _dt ? _dt : new Date();
    let tdt = new Date();
    let dayn = (dt.getDay() + 6) % 7;
    tdt.setDate(tdt.getDate() - dayn + 3);
    let firstThursday = tdt.valueOf();
    tdt.setMonth(0, 1);
    if (tdt.getDay() !== 4) {
        tdt.setMonth(0, 1 + ((4 - tdt.getDay()) + 7) % 7);
    }

    const weekNum = 1 + Math.ceil((firstThursday - tdt) / 604800000);
    const year = dt.getFullYear();

    return `${year}-W${numPad(weekNum)}`;
}

const numPad = (str) => {
    return String(str).length === 1 ? '0' + str : str;
}

const dateToString = (date) => {
    return `${date.getFullYear()}-${numPad(date.getMonth() + 1)}-${numPad(date.getDate())}`;
}

const timeToString = (date, seconds = false) => {
    const _seconds = seconds ? `:${numPad(date.getSeconds())}` : '00';
    return `${numPad(date.getHours())}:${numPad(date.getMinutes())}:${_seconds}`;
}

const dateTimeToString = (date, divider = ' ') => {
    return `${dateToString(date)}${divider}${timeToString(date)}`;
}

const useNewWindow = () => {
    // {request, isMobile}
    const mobile = useMobile();

    return (url) => {
        if (mobile && mobile.isMobile) {
            mobile.request('openUrl', { url });
        } else {
            window.open(url);
        }
    }
}

const useDownloadItemisation = (businessId, itemisationId) => {
    const newWindow = useNewWindow();

    return (businessId, itemisationId) => newWindow(
        `${API.domain.replace('/api/', '')}/itemisations/${businessId}/${itemisationId}.pdf`
    )
}

const useDownloadDocument = (documentId, key) => {
    const newWindow = useNewWindow();

    return (documentId, key) => newWindow(
        `${API.domain.replace('/api/', '')}/documents/${documentId}/${key}.pdf`
    )
}

const useDirectionsTo = () => {
    const newWindow = useNewWindow();

    return (address) => {
        newWindow(`https://www.google.com/maps/dir/?api=1&map_action=map&travelmode=driving&dir_action=navigate&destination=${encodeURIComponent(addressFormatter(address))}`)
    }
}

const ucFirst = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
}


const numFormat = (num, hideDecimals) => {
    if (num === null || num === undefined) {
        return 0;
    }
    const str = num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    if (hideDecimals) {
        return str.split('.')[0];
    } else if (hideDecimals === false) {
        const parts = str.split('.');
        if (parts[1] === undefined) {
            parts[1] = '00';
        } else if (parts[1] && parts[1].length < 2) {
            parts[1] += '0'.repeat(2 - parts[1].length);
        }
        return parts.join('.');
    }
    return str;
}

const plural = (count, zero, singular, plural) => {
    const int = parseInt(count ?? 0);
    if (int === 0 || isNaN(int)) {
        return zero;
    }

    if (int === 1) {
        return singular;
    }

    return plural;
}

const strToHsl = (str) => {
    if (!str) {
        return 'hsl(0, 50%, 50%)';
    }

    const h = str.split('').reduce((total, s) => {
        const x = total += s.charCodeAt(0);
        return x >= 360 ? x - 360 : x;
    }, 0);

    return `hsl(${h}, 100%, 45%)`;
}

const getContrastColor = (color, name) => {
    const _getVal = (_yiq) => {
        if (_yiq >= 128) {
            return name ? 'black' : '#000';
        } else {
            return name ? 'white' : '#FFF';
        }
    }

    // HSL
    if (color.indexOf(',') !== -1) {
        const [h, s, l] = color.split(',').map(v => v.replace('%', '').replace(' ', ''));
        const yiq = ((h * 299) + (s * 587) + (l * 114)) / 1000;
        return _getVal(yiq);
    }

    // Hex
    const hex = color.replace('#', '');
    var r = parseInt(hex.substr(0, 2), 16);
    var g = parseInt(hex.substr(2, 2), 16);
    var b = parseInt(hex.substr(4, 2), 16);
    var yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;

    return _getVal(yiq);
}

const b64SafeEncode = (str) => {
    return btoa(str).replace('+', '-').replace('/', '_').replace(/=+$/, '')
}

const b64SafeDecode = (str) => {
    return atob(str.replace('-', '+').replace('_', '/'));
}

const preloaded = [];
const preload = (path) => {
    if (preloaded.includes(path)) {
        return;
    }

    preloaded.push(path);

    const link       = document.createElement('link');
    link.as          = path.indexOf('.css') !== -1 ? 'style' : 'script';
    link.rel         = 'preload';
    link.href        = path;
    document.head.appendChild(link);
}

const stylesheets = [];
const stylesheetAliases = {
    fonts: 'https://getsoapy.com/css/fonts.css',
}
const loadStylesheet = (href) => {
    if (stylesheetAliases[href]) {
        href = stylesheetAliases[href];
    }

    if (stylesheets.includes(href)) {
        return;
    }

    stylesheets.push(href);

    const link       = document.createElement('link');
    link.rel          ='stylesheet';
    link.href        = href;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
}

const useOpenVideo = () => {
    const newWindow = useNewWindow();

    return (videoId) => {
        newWindow(videoId.replace(/^yt:/, 'https://www.youtube.com/watch?v='));
    }
}

const useRedirect = () => {
    const [redirect, setRedirect] = useState(null);

    useEffect(() => {
        if (!redirect) {
            return;
        }

        if (redirect === window.location.pathname) {
            setRedirect(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [window.location.pathname, redirect])

    return {
        go: setRedirect,
        Redirect: () => {
            if (redirect) {
                return <Navigate to={redirect} />
            }

            return null;
        }
    }
}

const useSearchParamsToObject = (searchParams, setter) => {
    useEffect(() => {
        if (!searchParams || !searchParams.entries()) {
            return;
        }
        const out = {};
        const entries = searchParams.entries();
        for (const [key, value] of entries) {
            if (key.substr(-2) === '[]') {
                const _key = key.substr(0, key.length - 2);
                if (!out[_key]) {
                    out[_key] = [];
                }
                out[_key].push(value);
                continue;
            }

            out[key] = value;
        }
        // Don't set if empty, as it will reset any persisted searches
        if (Object.keys(out).length === 0) {
            return;
        }

        setter(out);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);
}

const parseDuration = (duration) => {
    // We're dealing with seconds
    if (typeof duration === 'number') {
        const hours = Math.floor(duration / 3600);
        const minutes = Math.floor((duration % 3600) / 60);
        const seconds = duration % 60;
        return {hours, minutes, seconds, valid: true};
    }

    if (!duration || typeof duration !== 'string') {
        return {hours: 0, minutes: 0, seconds: 0, valid: false};
    }

    const parts = duration.split(':');
    const hours = parseInt(parts[0] ?? 0);
    const minutes = parseInt(parts[1] ?? 0);
    const seconds = parseInt(parts[2] ?? 0);

    return {hours, minutes, seconds, valid: true};
}

const durationToSeconds = (duration) => {
    const {hours, minutes, seconds} = parseDuration(duration);

    return (hours * 3600) + (minutes * 60) + seconds;
}

export {
    deviceIsMd,
    classNames,
    dateTimeToString,
    dateToString,
    getEntityLink,
    has,
    hashCode,
    isDocumentHidden,
    useDownloadItemisation,
    useDownloadDocument,
    useNewWindow,
    weekString,
    numPad,
    timeToString,
    toQueryString,
    useVisibilityChange,
    plural,
    ucFirst,
    numFormat,
    useDirectionsTo,
    strToHsl,
    getContrastColor,
    b64SafeEncode,
    b64SafeDecode,
    preload,
    loadStylesheet,
    isLocal,
    useOpenVideo,
    useRedirect,
    useSearchParamsToObject,
    parseDuration,
    durationToSeconds,
};
